import { ENUM__error_type } from '@shared/asserts/ENUM__error_type';
import { assert_false } from '@shared/asserts/utils_asserts';
import { MAP__serializable } from '@shared/dto/_base/MAP__serializable';
import { ENUM__invocation_role } from '../../_invocations/_base/ENUM__invocation_role';
import { INVOCATION__base } from '../../_invocations/_base/INVOCATION__base';
import { DTO_GRAPH__instance } from '../DTO_GRAPH__instance';

declare module '../DTO_GRAPH__instance' {
  interface DTO_GRAPH__instance {
    node__add<T extends INVOCATION__base>(node: T): T;

    node__get(id: string): INVOCATION__base;

    nodes__get(ids: string[]): INVOCATION__base[];

    nodes__get_by_type<T extends INVOCATION__base>(target_type: new (...args: any[]) => T): T[];

    node__delete(id: string): void;

    nodes__get_all(): INVOCATION__base[];

    // key - node_id
    nodes__get_map_incomers(node: INVOCATION__base, only_direct_connections: boolean): MAP__serializable<string, INVOCATION__base>;

    // key - node_id
    nodes__get_map_outgoers(node: INVOCATION__base, only_direct_connections: boolean): MAP__serializable<string, INVOCATION__base>;

    nodes__update_connected_to_grid2d(): void;

    node__is_connected_to_node_grid2d_view(node: INVOCATION__base): boolean;

    nodes__are_connected_directly(node_id_a: string, node_id_b: string): boolean;

    /*
     *
     */
    node_role__get_node(role: string): INVOCATION__base;

    node_role__has_node(role: string): boolean;

    node_role__get_next_for(role: string): string;
  }
}

export function INIT_EXTENSION_DTO_GRAPH__instance_nodes() {
  //do nothing
}

//#region Node Operations

/**
 * Add a node to the graph. If a node with the same id already exists, an `AssertionError` is raised.
 * The optional `is_intermediate` and `use_cache` fields are both set to `true`, if not set on the node.
 * @param node The node to add.
 * @returns The added node.
 * @raises `AssertionError` if a node with the same id already exists.
 */
DTO_GRAPH__instance.prototype.node__add = function <T extends INVOCATION__base>(node: T): T {
  if (node.meta.node_role != ENUM__invocation_role.UNKNOWN) assert_false(this.node_role__has_node(node.meta.node_role));

  let node_id = node.meta.node_id;

  assert_false(this.nodes_map.has(node_id), { assert_type: ENUM__error_type.GRAPH_NODE__ALREADY_EXISTS, node_id });

  node.fill_metadata(this);

  /*
   * SET LOWEST POSITION
   */
  let node_with_lowest_position: INVOCATION__base = node;

  for (let node of this.nodes_map.values()) {
    if (node.meta.graph_x_px + node.meta.graph_y_px < node_with_lowest_position.meta.graph_x_px + node_with_lowest_position.meta.graph_y_px) {
      node_with_lowest_position = node;
    }
  }

  [node.meta.graph_x_px, node.meta.graph_y_px] = [node_with_lowest_position.meta.graph_x_px, node_with_lowest_position.meta.graph_y_px];

  this.nodes_map.set(node_id, node);

  return node;
};

/**
 * Gets a node from the graph.
 * @param id The id of the node to get.
 * @returns The node.
 * @raises `AssertionError` if the node does not exist.
 */
DTO_GRAPH__instance.prototype.node__get = function (id: string): INVOCATION__base {
  return this.nodes_map.get_or_assert(id);
};

DTO_GRAPH__instance.prototype.nodes__get = function (ids: string[]): INVOCATION__base[] {
  let out = [];

  for (let id of ids) {
    let node = this.node__get(id);

    out.push(node);
  }

  return out;
};

DTO_GRAPH__instance.prototype.nodes__get_by_type = function <T extends INVOCATION__base>(target_type: new (...args: any[]) => T): T[] {
  let out: T[] = [];

  for (const node of this.nodes_map.values()) {
    if (node instanceof target_type) out.push(node as T);
  }

  return out;
};

/**
 * Deletes a node from the graph.
 * All edges to and from the node are also deleted.
 * @param id The id of the node to delete.
 */
DTO_GRAPH__instance.prototype.node__delete = function (id: string): void {
  this.nodes_map.get_or_assert(id); //check existance

  let edges = this.edges;
  edges = edges.filter(edge => edge.field_from__node_id == id || edge.field_to__node_id == id);
  this.edges__delete(edges);

  this.nodes_map.delete(id);

  this.grid2d__ensure_dimensions();
};

/**
 * Gets all nodes in the graph.
 * @returns An array of all nodes in the graph.
 */
DTO_GRAPH__instance.prototype.nodes__get_all = function (): INVOCATION__base[] {
  return this.nodes_map.values();
};

/**
 * Get the immediate incomers of a node.
 * @param node The node to get the incomers of.
 * @param only_direct_connections If true, return only direct connected nodes. If false, return all connected nodes from incoming side (for cycles handling).
 * @returns The incoming nodes as map (key - node_id).
 * @raises `AssertionError` if one of the target node's incoming edges has an invalid source node.
 */
DTO_GRAPH__instance.prototype.nodes__get_map_incomers = function (
  node: INVOCATION__base,
  only_direct_connections: boolean,
): MAP__serializable<string, INVOCATION__base> {
  let fields_map = node.fields__get_connectable(true);

  let edges_to = this.edges__get_to(fields_map.values());

  let out_array = edges_to.map(edge => this.node__get(edge.field_from__node_id));

  if (only_direct_connections === false) {
    let visited = new Set<string>(out_array.map(n => n.meta.node_id));
    let queue = [...out_array];
    let index = 0;

    while (index < queue.length) {
      let current = queue[index++];
      let current_fields = current.fields__get_connectable(true);
      let current_edges_to = this.edges__get_to(current_fields.values());

      for (const edge of current_edges_to) {
        let from_node_id = edge.field_from__node_id;

        if (visited.has(from_node_id)) continue;

        visited.add(from_node_id);
        queue.push(this.node__get(from_node_id));
      }
    }

    out_array = queue;
  }

  let out = new MAP__serializable<string, INVOCATION__base>();

  for (const node of out_array) out.set(node.meta.node_id, node);

  return out;
};

/**
 * Get the immediate outgoers of a node.
 * @param node The node to get the outgoers of.
 * @param only_direct_connections If true, return only direct connected nodes. If false, return all connected nodes from outgoing side (for cycles handling).
 * @returns The outgoing nodes as map (key - node_id).
 * @raises `AssertionError` if one of the target node's outgoing edges has an invalid destination node.
 */
DTO_GRAPH__instance.prototype.nodes__get_map_outgoers = function (
  node: INVOCATION__base,
  only_direct_connections: boolean,
): MAP__serializable<string, INVOCATION__base> {
  let edges_from = this.edges__get_from(node.fields__get_connectable(true).values());
  let out_array = edges_from.map(edge => this.node__get(edge.field_to__node_id));

  if (only_direct_connections === false) {
    let visited = new Set<string>(out_array.map(n => n.meta.node_id));
    let queue = [...out_array];
    let index = 0;

    while (index < queue.length) {
      let current = queue[index++];
      let current_fields = current.fields__get_connectable(true);
      let current_edges_from = this.edges__get_from(current_fields.values());

      for (const edge of current_edges_from) {
        let to_node_id = edge.field_to__node_id;
        if (visited.has(to_node_id)) continue;

        visited.add(to_node_id);
        queue.push(this.node__get(to_node_id));
      }
    }

    out_array = queue;
  }

  let out = new MAP__serializable<string, INVOCATION__base>();

  for (const node of out_array) out.set(node.meta.node_id, node);

  return out;
};

DTO_GRAPH__instance.prototype.nodes__update_connected_to_grid2d = function () {
  let graph_dagre = this.dagre__convert_to();

  let node_id__grid2d_view = this.invocation_grid2d_view.meta.node_id;

  for (let node of this.nodes_map.values()) {
    if (node.meta.node_id == node_id__grid2d_view) {
      node.meta.is_connected_to_grid2d = true;
      continue;
    }

    let connected_to_node_grid2d_view = this.dagre__nodes_are_connected(graph_dagre, node.meta.node_id, node_id__grid2d_view);

    node.meta.is_connected_to_grid2d = connected_to_node_grid2d_view;
  }
};

DTO_GRAPH__instance.prototype.node__is_connected_to_node_grid2d_view = function (node: INVOCATION__base) {
  let target_node_id = this.invocation_grid2d_view.meta.node_id;

  if (node.meta.node_id == target_node_id) return true;

  let graph_dagre = this.dagre__convert_to();

  let out = this.dagre__nodes_are_connected(graph_dagre, node.meta.node_id, target_node_id);

  return out;
};

DTO_GRAPH__instance.prototype.nodes__are_connected_directly = function (node_id_a: string, node_id_b: string): boolean {
  let out = this.edges.some(edge => edge.field_from__node_id == node_id_a && edge.field_to__node_id == node_id_b);

  out = out || this.edges.some(edge => edge.field_from__node_id == node_id_b && edge.field_to__node_id == node_id_a);

  return out;
};

/*
 *
 */
DTO_GRAPH__instance.prototype.node_role__get_node = function (role: string): INVOCATION__base {
  let out = this.nodes_map.values().find_or_assert(node => node.meta.node_role == role);

  return out;
};

DTO_GRAPH__instance.prototype.node_role__has_node = function (role: string): boolean {
  let out = this.nodes_map.values().some(node => node.meta.node_role == role);

  return out;
};

DTO_GRAPH__instance.prototype.node_role__get_next_for = function (role: string): string {
  if (role == ENUM__invocation_role.GRID2D__VIEW) return role; //only 1 grid2d view can be used
  if (role == ENUM__invocation_role.UNKNOWN) return role;

  if (!this.node_role__has_node(role)) return role;

  const match = role.match(/^(.*)_(\d+)$/);

  let [base_role, index]: [string, number] = match ? [match[1], Number(match[2]) + 1] : [role, 1];

  let [role_new, is_searching] = ['', true];

  while (is_searching) {
    role_new = `${base_role}_${index}`;

    if (!this.node_role__has_node(role_new)) {
      is_searching = false;
      break;
    }

    index++;
  }

  return role_new;
};
